'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { CriteoViewHomeProcutsDocument } from '@packages/gql/generated/shopping/CriteoViewHomeProcutsDocument';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { type CriteoPageId } from '@packages/gql/generated/shopping/graphql';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { useMediaType } from '@packages/shared/src/hooks/useMediaType/useMediaType';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel/RecoCarousel.client';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCard.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type CriteoViewHomeProductsRecoSliderProps = Omit<ProductsRecoSliderProps, 'preferTitle'>;

/* GraphQL */ `
  query CriteoViewHomeProcuts($pageId: CriteoPageId!) {
    criteoViewHome(params: { pageId: $pageId }) {
      type
      recommendations {
        ...RecoProductCard
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'criteoViewHome';

/**
 * CriteoViewHomeProductsRecoSlider component description displayed in storybook
 * */
export const CriteoViewHomeProductsRecoSlider = ({
  backgroundColor,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  title,
}: CriteoViewHomeProductsRecoSliderProps) => {
  const isDesktop = useMediaType();
  const { isInThreshold, isInView, recoEl } = useRecoElement();
  const hasCookieConsent = useCookieConsent('C0004');

  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';

  const pageId: CriteoPageId = (() => {
    if (isApp) return 'App';
    if (isDesktop) return 'Desktop';
    return 'Mobile';
  })();

  const [{ data, error }] = useQuery({
    query: CriteoViewHomeProcutsDocument,
    variables: {
      pageId,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: !hasCookieConsent || (fetchOnThreshold ? !isInThreshold : false),
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.criteoViewHome.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.criteoViewHome.type;

  useProductsRecoTracking({
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    !hasCookieConsent ||
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={idx}
          data={recommendation}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
          position={idx + 1}
          rdeTypeAbbr={rdeTypeAbbr}
        />
      ))}
    </RecoCarousel>
  );
};
